import {
  actingOf,
  bareTitle,
  bylineOf,
  labelOf,
  titleParts,
} from '@alphadesk/ontology/objects/notification.ts';
import { MINUTE_MS } from '@alphadesk/ontology/when.ts';
import type {
  TelegramState,
  WireBook,
  WireKindTally,
  WireNotification,
} from '@alphadesk/web/contract.ts';
import { PAGES } from '@alphadesk/web/links.ts';
import clsx from 'clsx';
import { Bell, Check, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNow } from '../lib/clock.ts';
import { istClock, istStamp, spanSaid } from '../lib/format.ts';
import { markRead, useInbox } from '../lib/inbox.ts';
import { alarmInk, type InboxLens, iconOf, isLensed, NO_LENS, useInboxLens } from '../lib/notes.ts';
import { useLinkClick } from '../lib/router.ts';
import { isScoped, NO_SCOPE, type Scope, scopeName, setScope, useScope } from '../lib/scope.ts';
import { useScrollEdges } from '../lib/screen.ts';
import { threadsOf } from '../lib/threads.ts';
import { InboxLensBar } from './InboxLens.tsx';
import { usePath } from './Invite.tsx';
import { NoteCopy, Words } from './NoteCopy.tsx';
import { Page } from './pages/Page.tsx';
import { Button } from './primitives/button.tsx';
import { linkClass } from './primitives/controls.tsx';
import { Empty } from './primitives/empty.tsx';
import { Count } from './primitives/marks.tsx';
import { HeaderMenu } from './primitives/panel.tsx';
import { EYEBROW, Eyebrow, RAIL } from './primitives/shell.tsx';
import { TelegramSection } from './Telegram.tsx';

function NoteLine({ row }: { row: WireNotification }) {
  const Icon = iconOf(row.kind);
  const unread = row.readAt === null;
  return (
    <li className="grid grid-cols-[2.75rem_0.875rem_1fr] items-baseline gap-x-2 text-xs leading-snug">
      <time dateTime={row.createdAt} className="tnum text-ink-faint">
        {istClock(row.createdAt)}
      </time>
      <Icon
        aria-label={labelOf(row.kind)}
        className={clsx(
          'size-3.5 translate-y-0.5',
          alarmInk(row) ?? (unread ? 'text-ink' : 'text-ink-faint'),
        )}
      />
      <span className={unread ? 'text-ink' : 'text-ink-muted'}>
        <span className={clsx(unread && 'font-semibold')}>{titleParts(row.title).what}</span>
        <span className="text-ink-muted">
          {' · '}
          <Words line={row.copy.lead} />
        </span>
      </span>
    </li>
  );
}

/** `rows` are newest first. Marked read only when followed or dismissed, never merely by being seen. */
function NotificationCard({
  rows,
  fold,
  onFollow,
  onRead,
}: {
  rows: readonly [WireNotification, ...WireNotification[]];
  fold?: 'glance' | 'rail';
  onFollow?: () => void;
  onRead?: (ids: number[]) => void;
}) {
  const now = useNow(MINUTE_MS);
  const [newest] = rows;
  const first = rows.at(-1) ?? newest;
  const later = rows.slice(0, -1).reverse();
  const acting = actingOf(rows);
  const unreadIds = rows.filter((row) => row.readAt === null).map((row) => row.id);
  const unread = unreadIds.length > 0;
  const Icon = iconOf(first.kind);
  const about = rows.find((row) => row.about !== null)?.about ?? null;
  const onClick = useLinkClick();
  const { viewing } = usePath();
  const markable = unread && !viewing;
  const read = () => {
    void markRead(unreadIds).catch(() => {});
    onRead?.(unreadIds);
  };
  const follow = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (markable) read();
    onFollow?.();
    onClick(event);
  };

  return (
    <article
      className={clsx(
        'flex flex-col gap-1.5 border-l-2 pl-3',
        unread ? 'border-accent/70' : 'border-line-soft',
      )}
    >
      <header className="flex items-start justify-between gap-2">
        <h3
          className={clsx(
            'flex items-start gap-2 text-xs leading-snug',
            first.readAt === null ? 'font-semibold text-ink' : 'text-ink-muted',
          )}
        >
          <span
            role="img"
            aria-label={labelOf(first.kind)}
            title={labelOf(first.kind)}
            className={clsx(
              'mt-0.5 shrink-0',
              alarmInk(first) ?? (first.readAt === null ? 'text-ink' : 'text-ink-faint'),
            )}
          >
            <Icon aria-hidden className="size-3.5" />
          </span>
          <span className="flex flex-col">
            <span>{bareTitle(first.title)}</span>
            {about && <span className="font-normal text-ink-faint">{bylineOf(about)}</span>}
          </span>
        </h3>
        <time
          dateTime={newest.createdAt}
          title={`${istStamp(newest.createdAt)} IST`}
          className="shrink-0 text-xs text-ink-faint"
        >
          {spanSaid(newest.createdAt, now)}
        </time>
      </header>
      <NoteCopy copy={first.copy} fold={fold} />
      {later.length > 0 && (
        <ol className="flex flex-col gap-1">
          {later.map((row) => (
            <NoteLine key={row.id} row={row} />
          ))}
        </ol>
      )}
      {(acting || markable) && (
        <div className="flex items-center justify-between gap-2">
          {acting?.href ? (
            <a
              href={acting.href}
              onClick={follow}
              className="inline-flex items-center gap-1 rounded-control border border-accent/40 px-2 py-0.5 text-xs font-medium text-accent transition-colors hover:bg-accent/10"
            >
              {acting.action ?? 'Open'}
              <ChevronRight aria-hidden className="size-3" />
            </a>
          ) : (
            <span />
          )}
          {markable && (
            <button
              type="button"
              onClick={read}
              className="inline-flex items-center gap-1 text-xs text-ink-faint hover:text-ink"
              title="Mark read"
            >
              <Check aria-hidden className="size-3" />
              Read
            </button>
          )}
        </div>
      )}
    </article>
  );
}

function Threads({
  rows,
  limit,
  ...card
}: { rows: readonly WireNotification[]; limit?: number } & Omit<
  Parameters<typeof NotificationCard>[0],
  'rows'
>) {
  const threads = useMemo(() => threadsOf(rows).slice(0, limit), [rows, limit]);
  return threads.map((thread) => (
    <NotificationCard key={thread.rows[0].id} rows={thread.rows} {...card} />
  ));
}

function MarkAllRead({ unread }: { unread: number }) {
  const { viewing } = usePath();
  if (unread === 0 || viewing) return null;
  return (
    <button
      type="button"
      onClick={() => void markRead(null).catch(() => {})}
      className="text-xs text-ink-faint hover:text-ink"
    >
      Mark all read
    </button>
  );
}

export function InboxBell({
  notifications,
  unread,
}: {
  notifications: WireNotification[];
  unread: number;
}) {
  const [open, setOpen] = useState(false);
  const onClick = useLinkClick();

  return (
    <HeaderMenu
      open={open}
      onDismiss={() => setOpen(false)}
      width="24rem"
      // Phones use a tab and wide screens the rail; the bell covers the widths between.
      className="hidden sm:block xl:hidden"
      trigger={
        <button
          type="button"
          aria-expanded={open}
          aria-label={unread > 0 ? `Inbox — ${unread} unread` : 'Inbox'}
          title="Inbox"
          onClick={() => setOpen((current) => !current)}
          className="relative rounded-control p-1.5 text-ink-faint transition-colors hover:bg-surface hover:text-ink"
        >
          <Bell className="size-4" />
          {unread > 0 && <Count n={unread} className="-top-0.5 -right-0.5 ring-2 ring-canvas" />}
        </button>
      }
    >
      <header className="flex items-center justify-between gap-2">
        <h3 className={EYEBROW}>Inbox</h3>
        <div className="flex items-center gap-2">
          <MarkAllRead unread={unread} />
          <a
            href={PAGES.notifications}
            onClick={(event) => {
              setOpen(false);
              onClick(event);
            }}
            className={linkClass('quiet', 'text-xs text-ink-muted')}
          >
            Open the inbox
          </a>
        </div>
      </header>
      {notifications.length === 0 ? (
        <Empty size="sm">Nothing yet.</Empty>
      ) : (
        <div className="scroll-quiet flex max-h-[60dvh] flex-col gap-3.5 overflow-y-auto overscroll-contain">
          <Threads rows={notifications} limit={8} fold="glance" onFollow={() => setOpen(false)} />
        </div>
      )}
    </HeaderMenu>
  );
}

export function InboxRail({
  notifications,
  unread,
  drawn,
}: {
  notifications: WireNotification[];
  unread: number;
  drawn: boolean;
}) {
  const onClick = useLinkClick();
  const edges = useScrollEdges();

  // An empty spacer keeps the page width stable on the inbox page.
  if (!drawn) return <div aria-hidden className="hidden w-80 shrink-0 xl:block" />;

  return (
    <aside aria-label="Inbox" className={RAIL}>
      <header className="flex shrink-0 items-baseline justify-between gap-2 pt-4 pr-4 pb-3">
        <Eyebrow as="h2">
          Inbox
          {unread > 0 && (
            <span className="tnum ml-2 font-mono text-xs tracking-normal text-ink-muted">
              {unread}
            </span>
          )}
        </Eyebrow>
        <MarkAllRead unread={unread} />
      </header>
      {notifications.length === 0 ? (
        <Empty size="sm" className="flex-1 pr-4">
          Nothing yet.
        </Empty>
      ) : (
        /* `min-h-0`, or the flex child will not shrink and the page scrolls instead of the rail. */
        <div
          ref={edges}
          className="scroll-quiet scroll-room scroll-fade flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto overscroll-contain pr-1 pb-1"
        >
          <Threads rows={notifications} fold="rail" />
        </div>
      )}
      <a
        href={PAGES.notifications}
        onClick={onClick}
        className={linkClass('row', 'text-xs text-ink-faint shrink-0 pt-3 pr-4 pb-4')}
      >
        Open the inbox
      </a>
    </aside>
  );
}

function ScopeNotice({ scope, books }: { scope: Scope; books: readonly WireBook[] }) {
  return (
    <p className="text-xs text-ink-muted">
      Only notes about <strong className="font-semibold text-ink">{scopeName(scope, books)}</strong>
      , as on Trades, and any unread alarm.{' '}
      <button type="button" onClick={() => setScope(NO_SCOPE)} className={linkClass()}>
        Show everything
      </button>
    </p>
  );
}

function LensMiss({ lens, onClear }: { lens: InboxLens; onClear: () => void }) {
  const sorted = lens.category !== null || lens.kind !== null;
  return (
    <Empty>
      {lens.unreadOnly && sorted
        ? 'Nothing unread of that sort.'
        : lens.unreadOnly
          ? 'Nothing unread.'
          : 'Nothing of that sort yet.'}{' '}
      <button type="button" className={linkClass()} onClick={onClear}>
        show everything
      </button>
    </Empty>
  );
}

export function InboxPage({
  notifications,
  receipts,
  unread: everywhere,
  tally,
  telegram,
  extend,
  books,
}: {
  notifications: WireNotification[];
  receipts: ReadonlyMap<number, string>;
  unread: number;
  tally: readonly WireKindTally[];
  telegram: TelegramState | null;
  extend: (older: WireNotification[]) => void;
  books: readonly WireBook[];
}) {
  const [lens, patch] = useInboxLens();
  const scope = useScope();
  const cut = useMemo(() => ({ lens, scope }), [lens, scope]);
  const inbox = useInbox(notifications, receipts, cut, extend);
  const { rows, done, loading, more, noteRead } = inbox;
  const unread = inbox.unread ?? everywhere;
  const { viewing } = usePath();

  const markAllRead = () => {
    void inbox.markAllRead().catch(() => {});
    noteRead(null);
  };

  return (
    <Page
      title="Inbox"
      lead={
        unread > 0
          ? `${unread} unread. Everything the desk told you unasked, newest first.`
          : 'Everything the desk told you unasked, newest first.'
      }
      aside={unread > 0 && !viewing && <Button onClick={markAllRead}>Mark all read</Button>}
    >
      {isScoped(scope) && <ScopeNotice scope={scope} books={books} />}
      <InboxLensBar lens={lens} patch={patch} tally={inbox.tally ?? tally} />
      {rows.length === 0 ? (
        isLensed(lens) ? (
          <LensMiss lens={lens} onClear={() => patch(NO_LENS)} />
        ) : isScoped(scope) ? (
          <Empty>Nothing about {scopeName(scope, books)} yet.</Empty>
        ) : (
          <Empty>
            Nothing yet. A recommendation landing, a level hit, anything your broker does — it
            arrives here.
          </Empty>
        )
      ) : (
        <div className="flex flex-col gap-3.5">
          <Threads rows={rows} onRead={noteRead} />
        </div>
      )}
      {rows.length > 0 && !done && (
        <div className="flex justify-center py-2">
          <Button busy={loading} onClick={() => void more()}>
            Load older
          </Button>
        </div>
      )}
      <TelegramSection framed={telegram} />
    </Page>
  );
}
